export const REAPPLY_REQUIREMENT_KEY = {
  BACKGROUND_CHECK: "background_check",
  QUALIFYING_LEVEL: "qualifying_level",
  PAT: "playing_ability_test",
  DUES: "dues",
}

export const REAPPLY_REQUIREMENT_COPY = {
  [REAPPLY_REQUIREMENT_KEY.BACKGROUND_CHECK]: {
    title: "Background Check",
    incomplete:
      "You must have a current approved background check to reapply as an Associate.",
    complete: "Your background check was approved.",
  },
  [REAPPLY_REQUIREMENT_KEY.QUALIFYING_LEVEL]: {
    title: "Qualifying Level",
    incomplete:
      "You must complete Qualifying Level to reapply as an Associate.",
    expired:
      "Your Qualifying Level has expired. You must complete it again to reapply as an Associate.",
    complete: "You have completed Qualifying Level.",
  },
  [REAPPLY_REQUIREMENT_KEY.PAT]: {
    title: "Playing Ability Test (PAT)",
    incomplete:
      "You must pass the Playing Ability Test to reapply as an Associate.",
    expired:
      "Your PAT score has expired. Register to try again when you are ready.",
    complete: "You have passed the Playing Ability Test.",
  },
  [REAPPLY_REQUIREMENT_KEY.DUES]: {
    title: "Dues",
    incomplete:
      "You must pay your outstanding dues balance to reapply as an Associate.",
    complete: "Your dues are current.",
  },
}

export const formatBalanceCents = cents =>
  (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  })

const isComplete = criterion => criterion?.complete === true

const buildRow = (key, { complete, expired = false, detail = null }) => {
  const copy = REAPPLY_REQUIREMENT_COPY[key]
  let description = copy.incomplete
  if (complete) description = copy.complete
  else if (expired && copy.expired) description = copy.expired

  return { key, title: copy.title, description, complete, detail }
}

/**
 * Rows for the Associate reapply checklist from Coach Tools
 * `associateApplicationEligibility`. Background Check and Qualifying Level are
 * stubbed complete for terminated Associates, so they only appear while
 * incomplete (dropped PGM students still owe them). Age is deliberately left
 * out: it is a rare edge case, and Coach Tools still enforces it on apply. Fails closed to no rows
 * when the eligibility payload is missing.
 */
export const buildAssociateReapplyRequirements = eligibility => {
  if (!eligibility) return []

  const rows = []

  if (!isComplete(eligibility.backgroundCheck)) {
    rows.push(
      buildRow(REAPPLY_REQUIREMENT_KEY.BACKGROUND_CHECK, {
        complete: false,
      }),
    )
  }

  if (!isComplete(eligibility.qualifyingLevel)) {
    rows.push(
      buildRow(REAPPLY_REQUIREMENT_KEY.QUALIFYING_LEVEL, {
        complete: false,
        expired: eligibility.qualifyingLevel?.expired === true,
      }),
    )
  }

  const pat = eligibility.playingAbilityTest
  const patComplete = isComplete(pat) && pat?.expired !== true
  rows.push(
    buildRow(REAPPLY_REQUIREMENT_KEY.PAT, {
      complete: patComplete,
      expired: pat?.expired === true,
    }),
  )

  const duesComplete = isComplete(eligibility.dues)
  const balanceCents = eligibility.dues?.balanceCents
  rows.push(
    buildRow(REAPPLY_REQUIREMENT_KEY.DUES, {
      complete: duesComplete,
      detail:
        !duesComplete && balanceCents > 0
          ? `Outstanding balance: ${formatBalanceCents(balanceCents)}`
          : null,
    }),
  )

  return rows
}

/**
 * Show the checklist when the person is on the Associate path (`pathEligible`)
 * but cannot start yet because a prerequisite is outstanding, and nothing else
 * already answers the question: no Apply/Resume CTA and no in-flight
 * application status. Leftover drafts and membership-gate blocks have
 * `pathEligible` false and stay hidden.
 *
 * @param {object} input
 * @param {object | null | undefined} input.eligibility Coach Tools Associate eligibility
 * @param {object | null | undefined} input.action `reapplyPathActionState` result for Associate
 * @param {boolean} input.hidden Reapply is blocked outright (re-establishment fee)
 */
export const resolveAssociateReapplyRequirements = ({
  eligibility,
  action,
  hidden = false,
}) => {
  if (hidden || !eligibility || !action) return []
  if (action.showApplyCta || action.replaceApplyCta) return []
  if (eligibility.pathEligible !== true || eligibility.eligible === true) {
    return []
  }

  const rows = buildAssociateReapplyRequirements(eligibility)
  return rows.some(row => !row.complete) ? rows : []
}
